import { ImmutableStore } from 'fluxible-immutable-utils';
import Actions from '../constants/Actions';
import Immutable from 'immutable';
import { startOfDay } from '../utils/Date';
import { parseJsonIfNeeded } from '../utils/UrlParamParsers';
import Settings from '../utils/Settings';

function defaultState() {
  return {
    guideType: 'both',
    guideDelivery: 'both',
    dataLoading: false,
    allProducts: [],
    categoryData: [],
    completedGuides: 0,
    conversions: 0,
    recommendedProducts: 0,
    openedProducts: 0,
    profitPotential: 0,
    profitCaptured: 0,
    aggregationType: Settings.envFeatures.get('bigBank') ? 'profit' : 'recommendations',
    productSortKey: 'count',
    productSortOrder: 'descending',
    selectedBranchHierarchy: null,
    customerType: 'both',
    includeAll: false
  };
}

function extractCategory(product) {
  const foundCategory = product.get('categories').find(c => c.get('primary'));
  return foundCategory ? foundCategory.get('name') : 'unknown';
}

function countProducts(products, field='count') {
  return products.map(p => p[field] || 0).reduce((a, b) => a + b, 0);
}

class ProductMixStore extends ImmutableStore {
  static storeName = 'ProductMixStore';

  static handlers = {
    [Actions.PRODUCT_MIX__QUERY_CHANGED]: 'handleProductMixQueryChanged',
    [Actions.PRODUCT_MIX__DATA_LOADED]: 'handleProductMixDataChanged',
    [Actions.PRODUCT_MIX__DATA_LOAD_STARTED]: 'handleLoadingData',
    [Actions.PRODUCTS__EXCLUDED_PRODUCTS_TOGGLED]: 'handleProductsExclude',
    [Actions.AUTHENTICATION__CHECK_COMPLETED]: 'handleAuth',
    [Actions.AUTHENTICATION__COMPLETED]: 'handleAuth'
  };

  constructor(dispatcher) {
    super(dispatcher);

    const now = startOfDay();
    const before = startOfDay().subtract(1, 'month');

    this.startDate = before.toDate();
    this.endDate = now.toDate();
    this.setState(defaultState());
  }

  handleProductMixQueryChanged(query) {
    const defaults = defaultState();
    const {
      focused,
      startDate = startOfDay().subtract(1, 'month'),
      endDate = startOfDay(),
      guideType = defaults.guideType,
      guideDelivery = defaults.guideDelivery,
      selectedCategory,
      aggregationType = defaults.aggregationType,
      productSortKey = defaults.productSortKey,
      productSortOrder = defaults.productSortOrder,
      selectedBranchHierarchy,
      customerType = defaults.customerType,
      includeAll
    } = query;

    if (startDate || endDate) {
      this.startDate = startDate ? new Date(startDate) : this.startDate;
      this.endDate = endDate ? new Date(endDate) : this.endDate;
      this.emitChange();
    }

    this.mergeState({
      guideType,
      focused,
      guideDelivery,
      selectedCategory,
      aggregationType,
      productSortKey,
      customerType,
      includeAll: includeAll === 'true',
      productSortOrder,
      selectedBranchHierarchy: parseJsonIfNeeded(selectedBranchHierarchy)
    });
  }

  handleLoadingData(type = 'data') {
    this.mergeState({
      [`${type}Loading`]: true
    });
  }

  handleProductMixDataChanged(payload) {
    const { productData } = payload;

    console.info('Updating productMix store state', payload);

    const state = {};

    if (productData) {
      state.completedGuides = productData.completedGuides;
      state.conversions = productData.conversions;
      state.recommendedProducts = countProducts(productData.recommendedProducts);
      state.openedProducts = countProducts(productData.recommendedProducts, 'opened');
      state.profitPotential = countProducts(productData.recommendedProducts, 'profitPotential');
      state.profitCaptured = countProducts(productData.recommendedProducts, 'profit');
      const categories = Immutable.fromJS(productData.recommendedProducts).groupBy(extractCategory);

      state.categoryData = categories.map(products => {
        const productArray = products.toJS();
        const numberOfRecommendations = countProducts(productArray);
        const numberOfOpens = countProducts(productArray, 'opened');
        const profitPotential = countProducts(productArray, 'profitPotential');
        const profitCaptured = countProducts(productArray, 'profit');

        return Immutable.fromJS({
          products: productArray,
          numberOfRecommendations,
          numberOfOpens,
          profitPotential,
          profitCaptured
        });
      });
      const currentCategory = this.getSelectedCategory();

      if (currentCategory && !categories.has(currentCategory)) {
        state.selectedCategory = null;
      }

      state.allProducts = productData.recommendedProducts;
      state.dataLoading = false;
    }

    this.mergeState(state);
  }

  getGuideType() {
    return this.get('guideType');
  }

  isIncludeAll() {
    return this.get('includeAll');
  }

  handleProductsExclude(value) {
    this.setState({includeAll: !!value});
  }

  getCategoryData() {
    return this.get('categoryData').toJS();
  }

  getDataForCategory(categoryName) {
    const data = this.get(['categoryData', categoryName]);

    return data ? data.toJS() : {
      products: this.get('allProducts').toJS(),
      numberOfRecommendations: this.getRecommendedProducts(),
      numberOfOpens: this.getOpenedProducts(),
      profitPotential: this.getProfitPotential(),
      profitCaptured: this.getProfitCaptured()
    };
  }

  getSelectedCategory() {
    return this.get('selectedCategory');
  }

  getSelectedCategoryData() {
    return this.getDataForCategory(this.get('selectedCategory'));
  }

  getSelectedDateRange() {
    return {
      start: this.startDate,
      end: this.endDate
    };
  }

  getGuideDelivery() {
    return this.get('guideDelivery');
  }

  isLoading() {
    return this.get('dataLoading');
  }

  getCompletedGuides() {
    return this.get('completedGuides');
  }

  getConversions() {
    return this.get('conversions');
  }

  getProfitPotential() {
    return this.get('profitPotential');
  }

  getProfitCaptured() {
    return this.get('profitCaptured');
  }

  getRecommendedProducts() {
    return this.get('recommendedProducts');
  }

  getOpenedProducts() {
    return this.get('openedProducts');
  }

  getCustomerType() {
    return this.get('customerType');
  }

  getProductsPerCustomer() {
    const guides = this.getCompletedGuides();
    if (!guides) {
      return 0;
    } else {
      return this.getRecommendedProducts() / guides;
    }
  }

  getProductsOpenedPerCustomer() {
    const guides = this.getConversions();
    if (!guides) {
      return 0;
    } else {
      return this.getOpenedProducts() / guides;
    }
  }

  getPotentialPerCustomer() {
    const guides = this.getCompletedGuides();
    if (!guides) {
      return 0;
    } else {
      return this.getProfitPotential() / guides;
    }
  }

  getCapturedPerCustomer() {
    const guides = this.getConversions();
    if (!guides) {
      return 0;
    } else {
      return this.getProfitCaptured() / guides;
    }
  }

  getAggregationType() {
    return this.get('aggregationType');
  }

  getProductSortKey() {
    return this.get('productSortKey');
  }

  getProductSortOrder() {
    return this.get('productSortOrder');
  }

  handleAuth = ({ clientInfo: { branchHierarchyTerms, branchHierarchy } = {} } = {}) => {
    if (branchHierarchyTerms && branchHierarchy) {
      this.mergeState({
        branchHierarchy,
        branchHierarchyTerms
      });
    }
  };

  getBranchHierarchy = () => {
    return this.get('branchHierarchy');
  };

  getBranchHierarchyTerms = () => {
    return this.get('branchHierarchyTerms');
  };

  getSelectedBranchHierarchy = () => {
    return this.get('selectedBranchHierarchy');
  };

  rehydrate(state) {
    super.rehydrate(state._state);

    this.startDate = new Date(state.startDate);
    this.endDate = new Date(state.endDate);
  }

  dehydrate() {
    return {
      _state: this._state,
      startDate: this.startDate,
      endDate: this.endDate
    };
  }
}

export default ProductMixStore;



// WEBPACK FOOTER //
// ./src/stores/ProductMixStore.js